import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import OrderFiles from './order-files/order-files.component';
import SampleMethod from 'components/lab-order/sample-method/sample-method.component';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import { Organization } from 'models/organization';
import { Sample } from 'models/order';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';
import { getPerfectDeliveryDate } from 'utils/disable-order-delivery-dates';
import { useParams } from 'react-router-dom';

type DeliveryFilesStepProps = {
    onNextClick: () => void;
    onBackClick: () => void;
};
const DeliveryFilesStep: FC<DeliveryFilesStepProps> = ({ onNextClick, onBackClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, updateOrder } = useContext(SupplierOrderContext);
    const [isValid, setIsValid] = useState(false);
    const [calculatedDate, setCalculatedDate] = useState<Date>();
    const params = useParams<{ id: string }>();

    useEffect(() => {
        if (order) {
            setIsValid(!!order.sample.type);
            return;
        }
        setIsValid(false);
    }, [order]);

    // Set perfect delivery date when lab and constructions are selected (only for new orders)
    useEffect(() => {
        if (!params.id && order?.supplier && order?.restorations?.length) {
            const perfectDate = getPerfectDeliveryDate(order.supplier, order.restorations);
            if (perfectDate && perfectDate !== order.deliveryDetails?.date) {
                setCalculatedDate(perfectDate);
                updateOrder({
                    ...order,
                    deliveryDetails: {
                        ...order.deliveryDetails,
                        date: perfectDate,
                    },
                });
            }
        }
    }, []);

    const handleDeliveryDateChange = (date: Date | null, hour: number | undefined, minute: number | undefined) => {
        updateOrder({
            ...order,
            deliveryDetails: {
                ...order.deliveryDetails,
                date,
                time: {
                    hour: hour,
                    minute: minute,
                },
            },
        });
    };

    const handleSampleSelection = (sample: Sample) => {
        updateOrder({
            ...order,
            sample,
        });
    };

    return (
        <Fragment>
            {order ? (
                <Fragment>
                    <Box>
                        <Box mb={2}>
                            <Typography variant="h1">{translate('orders:DELIVERY_FILES')}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Divider />
                        </Box>
                        <Box mb={4}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="h3" mb={1}>
                                            {translate('orders:DELIVERY_DATE')}
                                        </Typography>
                                    </Box>
                                    <DeliveryDatePicker
                                        onChange={handleDeliveryDateChange}
                                        date={order.deliveryDetails.date}
                                        calculatedDate={calculatedDate}
                                        hour={order.deliveryDetails.time.hour}
                                        minute={order.deliveryDetails.time.minute}
                                        lab={order?.supplier as Organization}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box mb={4}>
                            <Box>
                                <Typography variant="h3" mb={1}>
                                    {translate('orders:HOW_WAS_THE_SAMPLE_TAKEN')}
                                </Typography>
                            </Box>
                            <SampleMethod handleSampleSelection={handleSampleSelection} sample={order.sample} />
                        </Box>
                        <Box mb={2}>
                            <Box mb={1}>
                                <Typography variant="h3">{translate('common:FILES')}</Typography>
                            </Box>
                            {/* TODO: Should be able to use the shared file upload? */}
                            <OrderFiles />
                        </Box>
                    </Box>
                    <Box display="flex" mt={2}>
                        <Box flexGrow={1} textAlign="left">
                            <Button type="button" onClick={onBackClick}>
                                {translate('common:BACK')}
                            </Button>
                        </Box>
                        <Box>
                            <Button disabled={!isValid} type="button" onClick={onNextClick} variant="contained">
                                {translate('common:NEXT_STEP')}
                            </Button>
                        </Box>
                    </Box>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default DeliveryFilesStep;
